/**
 * PermissionOverridesTab Component
 * 
 * Admin panel tab for managing permission overrides
 * 
 * Features:
 * - List all active permission overrides
 * - Create new override (grant or deny)
 * - Edit existing override
 * - Remove override (soft delete)
 * - Filter by user, project, grant/deny status
 * 
 * Created: 2026-01-26 (Permission System V2 Phase 3)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const PermissionOverridesTab = () => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  
  // Data state
  const [overrides, setOverrides] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filterUser, setFilterUser] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterGranted, setFilterGranted] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    user_id: '',
    project_id: '',
    resource_type: '',
    permission: '',
    granted: true,
    reason: '',
    expires_at: ''
  });

  // Common permission suggestions
  const permissionSuggestions = [
    'view_all', 'edit_all', 'delete', 'approve', 'export',
    'matrix.upload', 'matrix.edit', 'matrix.delete', 'matrix.approve',
    'admin', 'manage_users', 'system_config',
    'upload', 'create', 'edit', 'view'
  ];

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filterUser) params.append('user_id', filterUser);
      if (filterProject) params.append('project_id', filterProject);
      if (filterGranted !== '') params.append('granted', filterGranted);
      params.append('active_only', filterActiveOnly);
      
      const [overridesRes, usersRes, projectsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/permissions/overrides?${params.toString()}`),
        authenticatedApiCall(instance, accounts, 'GET', '/users/all'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects')
      ]);
      
      if (overridesRes.data.success) {
        setOverrides(overridesRes.data.data || []);
      }
      if (usersRes.data.success) {
        setUsers(usersRes.data.data || []);
      }
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, filterUser, filterProject, filterGranted, filterActiveOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset form
  const resetForm = () => {
    setForm({
      user_id: '',
      project_id: '',
      resource_type: '',
      permission: '',
      granted: true,
      reason: '',
      expires_at: ''
    });
  };

  // Open create modal
  const handleOpenCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Open edit modal
  const handleOpenEdit = (override) => {
    setSelectedOverride(override);
    setForm({
      user_id: override.user_id,
      project_id: override.project_id || '',
      resource_type: override.resource_type || '',
      permission: override.permission,
      granted: override.granted,
      reason: override.reason || '',
      expires_at: override.expires_at ? override.expires_at.split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  // Create override
  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!form.user_id || !form.permission) {
      showToast('User and Permission are required', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        user_id: form.user_id,
        project_id: form.project_id || null,
        resource_type: form.resource_type || null,
        permission: form.permission,
        granted: form.granted,
        reason: form.reason || null,
        expires_at: form.expires_at || null
      };
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/permissions/overrides',
        payload
      );
      
      if (response.data.success) {
        showToast('Permission override created', 'success');
        setShowCreateModal(false);
        resetForm();
        fetchData();
      } else {
        showToast(response.data.message || 'Failed to create override', 'error');
      }
    } catch (err) {
      console.error('Error creating override:', err);
      showToast(err.response?.data?.message || 'Failed to create override', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Update override
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedOverride) return;
    
    try {
      setSubmitting(true);
      
      const payload = {
        granted: form.granted,
        reason: form.reason || null,
        expires_at: form.expires_at || null,
        resource_type: form.resource_type || null
      };
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/permissions/overrides/${selectedOverride.id}`,
        payload
      );
      
      if (response.data.success) {
        showToast('Permission override updated', 'success');
        setShowEditModal(false);
        setSelectedOverride(null);
        resetForm();
        fetchData();
      } else {
        showToast(response.data.message || 'Failed to update override', 'error');
      }
    } catch (err) {
      console.error('Error updating override:', err);
      showToast('Failed to update override', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Remove override
  const handleRemove = async (override) => {
    if (!window.confirm(`Are you sure you want to remove this override for "${override.permission}"?`)) {
      return;
    }
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/permissions/overrides/${override.id}`
      );
      
      if (response.data.success) {
        showToast('Override removed', 'success');
        fetchData();
      } else {
        showToast(response.data.message || 'Failed to remove override', 'error');
      }
    } catch (err) {
      console.error('Error removing override:', err);
      showToast('Failed to remove override', 'error');
    }
  };

  // Check if override is expired or expiring soon
  const getExpirationStatus = (expiresAt) => {
    if (!expiresAt) return null;
    
    const expDate = new Date(expiresAt);
    const now = new Date();
    const daysUntil = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return 'expired';
    if (daysUntil <= 7) return 'expiring-soon';
    return null;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  // Modal component
  const Modal = ({ title, show, onClose, children }) => {
    if (!show) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '500px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem',
            borderBottom: '1px solid #dee2e6'
          }}>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: '1rem' }}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  // Override form component
  const OverrideForm = ({ onSubmit, isEdit }) => (
    <form onSubmit={onSubmit}>
      {/* User Select (disabled in edit mode) */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          User *
        </label>
        <select
          value={form.user_id}
          onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          disabled={isEdit}
          required
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            backgroundColor: isEdit ? '#f8f9fa' : 'white'
          }}
        >
          <option value="">Select User</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.display_name || user.email}
            </option>
          ))}
        </select>
      </div>

      {/* Project Select */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          Project (leave empty for global)
        </label>
        <select
          value={form.project_id}
          onChange={(e) => setForm({ ...form, project_id: e.target.value })}
          disabled={isEdit}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            backgroundColor: isEdit ? '#f8f9fa' : 'white'
          }}
        >
          <option value="">Global (All Projects)</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>
              {project.job_number} - {project.job_name}
            </option>
          ))}
        </select>
      </div>

      {/* Permission Input */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          Permission *
        </label>
        <input
          type="text"
          value={form.permission}
          onChange={(e) => setForm({ ...form, permission: e.target.value })}
          disabled={isEdit}
          required
          list="permission-suggestions"
          placeholder="e.g., matrix.upload, edit_all, approve"
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            backgroundColor: isEdit ? '#f8f9fa' : 'white'
          }}
        />
        <datalist id="permission-suggestions">
          {permissionSuggestions.map(p => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </div>

      {/* Resource Type */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          Resource Type (optional)
        </label>
        <input
          type="text"
          value={form.resource_type}
          onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
          placeholder="e.g., ProgressTracking, Budget"
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}
        />
      </div>

      {/* Grant/Deny Toggle */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
          Override Type
        </label>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            backgroundColor: form.granted ? '#d4edda' : '#f8f9fa',
            border: form.granted ? '2px solid #28a745' : '1px solid #ddd',
            borderRadius: '4px'
          }}>
            <input
              type="radio"
              name="granted"
              checked={form.granted === true}
              onChange={() => setForm({ ...form, granted: true })}
            />
            <span style={{ color: '#28a745', fontWeight: form.granted ? 600 : 400 }}>
              Grant Permission
            </span>
          </label>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            backgroundColor: !form.granted ? '#f8d7da' : '#f8f9fa',
            border: !form.granted ? '2px solid #dc3545' : '1px solid #ddd',
            borderRadius: '4px'
          }}>
            <input
              type="radio"
              name="granted"
              checked={form.granted === false}
              onChange={() => setForm({ ...form, granted: false })}
            />
            <span style={{ color: '#dc3545', fontWeight: !form.granted ? 600 : 400 }}>
              Deny Permission
            </span>
          </label>
        </div>
      </div>

      {/* Expiration Date */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          Expires At (optional)
        </label>
        <input
          type="date"
          value={form.expires_at}
          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}
        />
        <small style={{ color: '#666' }}>Leave empty for permanent override</small>
      </div>

      {/* Reason */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
          Reason
        </label>
        <textarea
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Why is this override being granted/denied?"
          rows={3}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Submit Button */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            resetForm();
          }}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: submitting ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem'
          }}
        >
          {submitting ? 'Saving...' : (isEdit ? 'Update Override' : 'Create Override')}
        </button>
      </div>
    </form>
  );

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem' 
      }}>
        <div>
          <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Permission Overrides</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Grant or deny specific permissions on a per-user, per-project basis.
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          + Create Override
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            minWidth: '200px'
          }}
        >
          <option value="">All Users</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.display_name || user.email}
            </option>
          ))}
        </select>

        <select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            minWidth: '200px'
          }}
        >
          <option value="">All Projects</option>
          <option value="null">Global Only</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>
              {project.job_number} - {project.job_name}
            </option>
          ))}
        </select>

        <select
          value={filterGranted}
          onChange={(e) => setFilterGranted(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}
        >
          <option value="">All Types</option>
          <option value="true">Grants Only</option>
          <option value="false">Denies Only</option>
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={filterActiveOnly}
            onChange={(e) => setFilterActiveOnly(e.target.checked)}
          />
          Active Only
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Loading overrides...
        </div>
      ) : overrides.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          No permission overrides found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>User</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Project</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Permission</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Type</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Expires</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Reason</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map(override => {
                const expStatus = getExpirationStatus(override.expires_at);
                
                return (
                  <tr key={override.id} style={{ 
                    backgroundColor: expStatus === 'expired' ? '#f8d7da' : 
                                     expStatus === 'expiring-soon' ? '#fff3cd' : 'white'
                  }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {override.user_name || override.user_email}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {override.job_number ? `${override.job_number}` : 
                       <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Global</span>}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      <code style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.85rem'
                      }}>
                        {override.permission}
                      </code>
                      {override.resource_type && (
                        <small style={{ display: 'block', color: '#666' }}>
                          Resource: {override.resource_type}
                        </small>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: override.granted ? '#d4edda' : '#f8d7da',
                        color: override.granted ? '#155724' : '#721c24',
                        borderRadius: '4px',
                        fontWeight: 500,
                        fontSize: '0.85rem'
                      }}>
                        {override.granted ? 'GRANT' : 'DENY'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      {expStatus === 'expired' && (
                        <span style={{ color: '#dc3545', fontWeight: 500 }}>Expired</span>
                      )}
                      {expStatus === 'expiring-soon' && (
                        <span style={{ color: '#856404', fontWeight: 500 }}>
                          {formatDate(override.expires_at)}
                        </span>
                      )}
                      {!expStatus && formatDate(override.expires_at)}
                    </td>
                    <td style={{ 
                      padding: '0.75rem', 
                      borderBottom: '1px solid #dee2e6',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={override.reason}>
                      {override.reason || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleOpenEdit(override)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemove(override)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        title="Create Permission Override"
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
      >
        <OverrideForm onSubmit={handleCreate} isEdit={false} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Permission Override"
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedOverride(null);
          resetForm();
        }}
      >
        <OverrideForm onSubmit={handleUpdate} isEdit={true} />
      </Modal>
    </div>
  );
};

export default PermissionOverridesTab;
